import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import ApiClient from "./ApiClient";
import "../src/css/Navbar.css";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "./stores/userSlice";
import TailwindModal, { TailwindModalBody } from "./components/TailwindModal";
import {
  fetchNotificationAction,
  EditNotificationAction,
  selectNotification,
} from "./stores/notificationSlice";
import { Tooltip } from "@mui/material";
import { Bell, CircleHelp, CalendarRange, LogOut, Menu, X, AlertCircle, CalendarDays, Calendar, Clock, User, Settings } from "lucide-react";
import FullLogo from './images/DAFullLogo.png';
import UserAvatar from "./components/UserAvatar";

const EXPIRE_TIME_MINS = 1000 * 60 * 60;

const getHeaderContent = (pathname) => {
    if (pathname.includes('/dashboard')) return { title: 'Daily Status Dashboard', subtitle: 'Track work hours, daily activities, attendance, leave, and important alerts in one place.' };
    if (pathname.includes('/daily-status')) return { title: 'Daily Status', subtitle: 'Log and track your daily tasks and accomplishments.' };
    if (pathname.includes('/leave')) return { title: 'Leave Management', subtitle: 'Apply for leave, WFH, and view office holidays.' };
    if (pathname.includes('/our-team') || pathname.includes('/team')) return { title: 'Our Team', subtitle: 'View team members, hierarchy, and contact information.' };
    if (pathname.includes('/my-account')) return { title: 'My Account', subtitle: 'Manage your personal profile and settings.' };
    if (pathname.includes('/account-settings')) return {
        title: 'Account Settings',
        subtitle: 'Manage your account preferences, security, and settings.'
    };
    if (pathname.includes('/admin')) return { title: 'Manager', subtitle: 'Manage employees, projects, and system configurations.' };
    if (pathname.includes('/super-admin')) return { title: 'Super Admin', subtitle: 'System-wide administrative controls and data management.' };
    if (pathname.includes('/hr-panel')) return { title: 'HR Panel', subtitle: 'Human Resources dashboard and employee management.' };
    if (pathname.includes('/queries')) return { title: 'Queries', subtitle: 'Submit and track your HR and IT queries.' };
    if (pathname.includes('/policies')) return { title: 'Company Policies', subtitle: 'View and acknowledge company policies and guidelines.' };
    return null;
}

const Navbar = ({ toggleMobileMenu, isMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const notification = useSelector(selectNotification);
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [profPic, setProfPic] = useState("");
  const [lastLogin, setLastLogin] = useState("");
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [isEventsOpen, setIsEventsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const alertsRef = useRef(null);
  const eventsRef = useRef(null);
  const profileRef = useRef(null);
  
  const logout = () => {
    redirectToLogin();
  };

  const getProfileImage = () => {
    let img_path = user?.img_dir
      ? `${process.env.REACT_APP_PUBLIC_URL}/${user?.img_dir}?t=${Date.now()}`
      : "";
    setProfPic(img_path);
  };

  const redirectToLogin = () => {
    window.location.href = "/";
    localStorage.removeItem("authToken");
    localStorage.removeItem('da_stepUpToken');
    sessionStorage.removeItem('da_stepUpToken');
  };

  useEffect(() => {
    setTimeout(() => {
      redirectToLogin();
    }, EXPIRE_TIME_MINS);
  });

  const viewLogout = () => {
    setOpen(true);
    setDescription("Are you sure you want to quit?");
  };
  const dispatch = useDispatch();

  useEffect(() => {
    function handleClickOutside(event) {
      if (alertsRef.current && !alertsRef.current.contains(event.target)) {
        setIsAlertsOpen(false);
      }
      if (eventsRef.current && !eventsRef.current.contains(event.target)) {
        setIsEventsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenAlerts = async () => {
    setIsAlertsOpen(!isAlertsOpen);
    setIsEventsOpen(false);
    if (!isAlertsOpen && notification?.Alerts === "Yes" && notification?.AlertsData?.length > 0) {
       dispatch(EditNotificationAction({ ...notification, Alerts: "No" }));
       try {
          await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/dashboard/readAlertsOrEvents`, {
             Unread: notification.AlertsData,
             empcode: user.emp_code
          });
       } catch (error) { console.error(error); }
    }
  };

  const handleOpenEvents = async () => {
    setIsEventsOpen(!isEventsOpen);
    setIsAlertsOpen(false);
    if (!isEventsOpen && notification?.Events === "Yes" && notification?.EventsData?.length > 0) {
       dispatch(EditNotificationAction({ ...notification, Events: "No" }));
       try {
          await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/dashboard/readAlertsOrEvents`, {
             Unread: notification.EventsData,
             empcode: user.emp_code
          });
       } catch (error) { console.error(error); }
    }
  };

  useEffect(() => {
    getProfileImage();
    if (user) {
      dispatch(fetchNotificationAction(user.emp_code));
    }
    if (user?.LAST_LOGIN) {
      const date = new Date(user?.LAST_LOGIN);
      const hours = String(date.getHours() % 12 || 12); // Get 12-hour format
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const period = date.getHours() >= 12 ? "pm" : "am";

      const formattedDate = `${hours}:${minutes} ${period}`;
      setLastLogin(formattedDate);
    }
  }, [user]);

  const headerContent = getHeaderContent(location.pathname);

  return (
    <header className="sticky top-0 z-50 bg-white md:bg-[#f6f6f6] border-b border-slate-100 md:border-b-0 h-[60px] md:h-auto flex items-center">
      <div className="flex flex-row items-center justify-between px-4 sm:px-6 sm:py-2 w-full gap-2">
        {/* Mobile Hamburger Menu */}
        <button 
          className="md:hidden mr-1 sm:mr-2 p-1 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none flex-shrink-0" 
          onClick={toggleMobileMenu}
          aria-label="Toggle Menu"
        >
          {isMobileMenuOpen ? <X strokeWidth={2.5} size={24} /> : <Menu strokeWidth={2.5} size={24} />}
        </button>

        {/* Desktop Title */}
        {headerContent ? (
          <div className="hidden md:flex min-w-0 flex-1 mr-2">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">
              {headerContent.title}
            </h1>
          </div>
        ) : (
          <div className="hidden md:flex flex-1" />
        )}

        {/* Mobile Logo */}
        <div className="md:hidden flex-1 flex justify-start ms-2">
          <Link to="/dashboard">
            <img src={FullLogo} alt="Data Aces" className="h-6 object-contain" />
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-5 shrink-0">
            {/* Alerts */}
            <div className="relative shrink-0" ref={alertsRef}>
              <Tooltip title="Alerts">
                <button
                  onClick={handleOpenAlerts}
                  className="relative p-1.5 text-slate-500 hover:text-brand-600 transition-colors flex items-center justify-center rounded-full hover:bg-slate-100 focus:outline-none"
                >
                  <Bell strokeWidth={1.7} size={22} />
                  {notification?.Alerts === "Yes" ? (
                    <span className="absolute top-1 right-1.5 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white"></span>
                    </span>
                  ) : null}
                </button>
              </Tooltip>
              {isAlertsOpen && (
                <div className="absolute top-[120%] right-[-60px] sm:right-[-10px] mt-2 w-[340px] bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 z-50 flex flex-col">
                  {/* Tooltip Arrow */}
                  <div className="absolute -top-[6px] right-[74px] sm:right-[18px] w-3 h-3 bg-white rotate-45 border-l border-t border-slate-100"></div>
                  
                  <div className="px-5 pt-4 pb-3 flex flex-col relative z-10 bg-white rounded-t-[14px] border-b border-slate-100">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <h3 className="font-bold text-slate-900 text-[15px]">Notifications</h3>
                        {notification?.AlertsData?.length > 0 && (
                           <span className="ml-2 bg-[#ff6b6b] text-white text-[11px] font-bold px-2 py-0.5 rounded-full">{notification.AlertsData.length}</span>
                        )}
                      </div>
                      <button 
                        onClick={() => { setIsAlertsOpen(false); navigate("/dashboard/alerts"); }}
                        className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
                      >
                        View all <span className="text-[15px]">&rarr;</span>
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[340px] flex flex-col relative z-10 custom-scrollbar rounded-b-[14px]">
                    {notification?.AlertsData?.length > 0 ? (
                      notification.AlertsData.map((alert, idx) => (
                        <div key={idx} className="px-5 py-3.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 flex gap-4 bg-white">
                           <div className={`w-8 h-8 rounded-2xl flex items-center justify-center shrink-0 ${(alert?.TITLE || '').toLowerCase().includes('daily status') ? 'bg-[#fff4ed] text-orange-500' : 'bg-[#fff0f0] text-red-500'}`}>
                              {(alert?.TITLE || '').toLowerCase().includes('daily status') ? <CalendarDays className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                           </div>
                           <div className="flex-1 min-w-0 pt-0.5">
                              <p className="text-[12px] text-slate-500 truncate">{alert.DESCRIPTION}</p>
                           </div>
                           <div className="flex items-start gap-1.5 shrink-0 pt-0.5">
                              <span className="text-[11px] text-slate-400 mt-[2px]">{moment(alert.RAISED_DATE).fromNow(true)} ago</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5"></span>
                           </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-5 py-8 text-center bg-white">
                        <p className="text-sm text-slate-500">No new notifications at the moment.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Events */}
            <div className="relative shrink-0" ref={eventsRef}>
              <Tooltip title="Events">
                <button
                  onClick={handleOpenEvents}
                  className="relative p-1.5 text-slate-500 hover:text-brand-600 transition-colors flex items-center justify-center ml-1 rounded-full hover:bg-slate-100 focus:outline-none"
                >
                  <CalendarRange strokeWidth={1.7} size={22} />
                  {notification?.Events === "Yes" ? (
                    <span className="absolute top-1 right-1.5 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white"></span>
                    </span>
                  ) : null}
                </button>
              </Tooltip>
              {isEventsOpen && (
                <div className="absolute top-[120%] right-[-20px] sm:right-[-10px] mt-2 w-[340px] bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 z-50 flex flex-col">
                  {/* Tooltip Arrow */}
                  <div className="absolute -top-[6px] right-[34px] sm:right-[18px] w-3 h-3 bg-white rotate-45 border-l border-t border-slate-100"></div>
                  
                  <div className="px-5 pt-4 pb-3 flex flex-col relative z-10 bg-white rounded-t-[14px] border-b border-slate-100">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <h3 className="font-bold text-slate-900 text-[15px]">Events</h3>
                        {notification?.EventsData?.length > 0 && (
                           <span className="ml-2 bg-[#ff6b6b] text-white text-[11px] font-bold px-2 py-0.5 rounded-full">{notification.EventsData.length}</span>
                        )}
                      </div>
                      <button 
                        onClick={() => { setIsEventsOpen(false); navigate("/team/upcomingEvents"); }}
                        className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
                      >
                        View all <span className="text-[15px]">&rarr;</span>
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[340px] flex flex-col relative z-10 custom-scrollbar rounded-b-[14px]">
                    {notification?.EventsData?.length > 0 ? (
                      notification.EventsData.map((evt, idx) => {
                          let eventDetails = null;
                          if (evt.EVENT_DETAILS) {
                              try {
                                  eventDetails = typeof evt.EVENT_DETAILS === 'string' ? JSON.parse(evt.EVENT_DETAILS) : evt.EVENT_DETAILS;
                              } catch(e) {}
                          }
                          const title = eventDetails?.title || evt.TITLE || evt.DESCRIPTION;
                          const mEventDate = eventDetails?.event_date ? moment(eventDetails.event_date) : moment(evt.RAISED_DATE);
                          const monthStr = mEventDate.format('MMM');
                          const dayStr = mEventDate.format('DD');
                          const weekdayStr = mEventDate.format('ddd');
                          const eventType = eventDetails?.event_type || 'Event';
                          let timeStr = null;
                          if (eventDetails?.start_time && eventDetails?.end_time) {
                              timeStr = `${moment(eventDetails.start_time, "HH:mm").format("hh:mm A")} - ${moment(eventDetails.end_time, "HH:mm").format("hh:mm A")}`;
                          }
                          
                          return (
                      <div
                        key={idx}
                        className="px-5 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <div className="grid grid-cols-[42px_minmax(0,1fr)_auto] gap-3 items-start">

                          {/* Date Block */}
                          <div className="flex flex-col items-center w-[42px] rounded-xl border border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] shrink-0 mt-0.5 overflow-hidden bg-white">
                              <span className="w-full text-center bg-blue-500 text-white text-[9px] font-medium uppercase tracking-widest py-[3px] leading-none">{monthStr}</span>
                              <div className="flex flex-col items-center justify-center w-full py-1.5">
                                  <span className="text-[15px] font-bold text-slate-700 leading-none">{dayStr}</span>
                              </div>
                          </div>

                          {/* Main content */}
                          <div className="min-w-0">

                            {/* Row 1: Title */}
                            <div className="min-w-0">
                              <h4 className="text-[13px] font-bold text-slate-800 truncate mb-0">
                                {title}
                              </h4>
                            </div>

                            {/* Row 2: Description */}
                            {evt.DESCRIPTION && (
                              <p className="text-[12px] text-slate-500 truncate mt-1">
                                {evt.DESCRIPTION}
                              </p>
                            )}

                            {/* Row 3: Time */}
                            {timeStr && (
                              <div className="flex items-center gap-3 mt-1.5">
                                <div className="flex items-center gap-1 text-[10.5px] text-slate-500 font-medium">
                                  <Clock className="w-[11px] h-[11px] text-slate-400" />
                                  <span>{timeStr}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Timestamp + Event Type */}
                          <div className="flex flex-col items-end gap-1 pt-1 shrink-0">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              <span className="text-[10px] text-slate-400">
                                {moment(evt.RAISED_DATE).fromNow(true)} ago
                              </span>
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            </div>

                            {eventType && (
                              <span className="mt-1 px-1.5 py-0.5 bg-purple-50 text-purple-600 border border-purple-100 rounded text-[9px] font-bold uppercase tracking-wide">
                                {eventType}
                              </span>
                            )}
                          </div>

                        </div>
                      </div>
                          );
                      })
                    ) : (
                      <div className="px-5 py-8 text-center bg-white">
                        <p className="text-sm text-slate-500">No new events at the moment.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>


            <div className="relative shrink-0 flex items-center" ref={profileRef}>
               <button
                 className="flex items-center gap-2 sm:gap-3 p-1 rounded-full sm:rounded-xl hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                 onClick={() => setIsProfileOpen(!isProfileOpen)}
               >
                 <div className="relative shrink-0">
                   <div className="w-8 h-8 sm:w-10 sm:h-10 shadow-sm rounded-full overflow-hidden shrink-0">
                     <UserAvatar
                       name={user?.name}
                       imageUrl={profPic}
                       size="100%"
                     />
                   </div>
                   <span className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full"></span>
                 </div>
                 <div className="flex-col items-start hidden lg:flex text-left">
                    <span className="text-[13px] sm:text-[14px] font-bold text-slate-800 leading-tight tracking-wide truncate max-w-[120px]">{user?.name?.toUpperCase()}</span>
                    {lastLogin != "" ? (
                       <span className="text-[10px] sm:text-[12px] text-slate-500 font-medium mt-0.5 truncate max-w-[120px]">Logged in {lastLogin}</span>
                    ) : null}
                 </div>
               </button>

               {isProfileOpen && (
                 <div className="absolute top-[120%] right-0 mt-2 w-[260px] bg-white rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.13)] border border-slate-100 z-50 flex flex-col overflow-hidden">
                   <div className="absolute -top-[6px] right-[18px] w-3 h-3 bg-white rotate-45 border-l border-t border-slate-100"></div>

                   {/* Avatar + Identity Header */}
                   <div className="px-5 pt-5 pb-4 flex items-center gap-3 relative z-10 bg-white border-b border-slate-100">
                     <div className="relative shrink-0">
                       <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-slate-100 shadow-sm">
                         <UserAvatar name={user?.name} imageUrl={profPic} size="100%" />
                       </div>
                       <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                     </div>
                     <div className="min-w-0 flex-1">
                       <p className="text-[14px] font-bold text-slate-900 truncate leading-tight">{user?.name}</p>
                       <p className="text-[11.5px] text-slate-500 truncate mt-0.5">{user?.username}</p>
                       {user?.role && (
                         <span className="inline-block px-2 py-0.5 text-[12px] text-slate-500 uppercase">
                           {user.role}
                         </span>
                       )}
                     </div>
                   </div>

                   {/* Menu Items */}
                   <div className="flex flex-col py-2 relative z-10">
                     <button onClick={() => { setIsProfileOpen(false); navigate("/my-account"); }} className="px-5 py-2.5 hover:bg-slate-50 transition-colors text-left flex items-center gap-3 text-[13px] font-medium text-slate-700">
                       <User size={15} className="text-slate-400 shrink-0" />
                       My Profile
                     </button>
                     <button onClick={() => { setIsProfileOpen(false); navigate("/account-settings"); }} className="px-5 py-2.5 hover:bg-slate-50 transition-colors text-left flex items-center gap-3 text-[13px] font-medium text-slate-700">
                       <Settings size={15} className="text-slate-400 shrink-0" />
                       Account Settings
                     </button>
                     <div className="my-1 mx-3 border-b border-slate-100"></div>
                     <button onClick={() => { setIsProfileOpen(false); viewLogout(); }} className="px-5 py-2.5 hover:bg-red-50 transition-colors text-left flex items-center gap-3 text-[13px] font-medium text-red-600">
                       <LogOut size={15} className="text-red-400 shrink-0" />
                       Logout
                     </button>
                   </div>
                 </div>
               )}
            </div>
        </div>
      </div>

      <TailwindModal
        isOpen={open}
        toggle={() => setOpen(!open)}
        maxWidth="sm"
      >
        <TailwindModalBody>
          <div className="mb-4">
            <strong className="block text-xl mb-2 text-slate-900">Log Out</strong>
            <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: description }} />
          </div>
          <div className="flex justify-end gap-3 mt-8">
            <button 
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-slate-300"
              onClick={() => setOpen(!open)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              onClick={() => logout()}
            >
              Yes, Logout
            </button>
          </div>
        </TailwindModalBody>
      </TailwindModal>
    </header>
  );
};

export default Navbar;
